import { supabase } from "@/integrations/supabase/client";

export interface VideoCloneRequest {
  id: string;
  user_id: string;
  video_url: string | null;
  video_file_name: string | null;
  description: string;
  status: 'pending' | 'processing' | 'completed' | 'rejected';
  credits_used: number;
  admin_notes: string | null;
  result_prompts: string[] | null;
  admin_response: string | null;
  submitted_at: string;
  completed_at: string | null;
  admin_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface VideoCloneRequestWithUser extends VideoCloneRequest {
  profiles: {
    email: string;
    display_name: string | null;
  } | null;
}

/**
 * Create a new video clone request (user function)
 * This will deduct 110 credits from the user
 */
export async function createVideoCloneRequest(
  userId: string,
  videoUrl: string | null,
  videoFileName: string | null,
  description: string
): Promise<string | null> {
  const { data, error } = await supabase.rpc('create_video_clone_request', {
    p_user_id: userId,
    p_video_url: videoUrl || null,
    p_video_file_name: videoFileName || null,
    p_description: description
  });

  if (error) throw error;
  return data as string | null;
}

/**
 * Get all video clone requests for a user
 */
export async function getUserVideoCloneRequests(
  userId: string
): Promise<VideoCloneRequest[]> {
  try {
    const { data, error } = await supabase
      .from('video_clone_requests')
      .select('*')
      .eq('user_id', userId)
      .order('submitted_at', { ascending: false });

    if (error) throw error;
    return (data || []) as VideoCloneRequest[];
  } catch (error) {
    console.error('Error getting user video clone requests:', error);
    return [];
  }
}

/**
 * Get all video clone requests (admin function)
 */
export async function getAllVideoCloneRequests(): Promise<VideoCloneRequestWithUser[]> {
  try {
    const { data, error } = await supabase
      .from('video_clone_requests')
      .select(`
        *,
        profiles (
          email,
          display_name
        )
      `)
      .order('submitted_at', { ascending: false });

    if (error) throw error;
    return (data || []) as VideoCloneRequestWithUser[];
  } catch (error) {
    console.error('Error getting all video clone requests:', error);
    return [];
  }
}

/**
 * Get pending video clone requests (admin function)
 */
export async function getPendingVideoCloneRequests(): Promise<VideoCloneRequestWithUser[]> {
  try {
    const { data, error } = await supabase
      .from('video_clone_requests')
      .select(`
        *,
        profiles (
          email,
          display_name
        )
      `)
      .in('status', ['pending', 'processing'])
      .order('submitted_at', { ascending: true });

    if (error) throw error;
    return (data || []) as VideoCloneRequestWithUser[];
  } catch (error) {
    console.error('Error getting pending video clone requests:', error);
    return [];
  }
}

/**
 * Update video clone request with admin response (admin function)
 */
export async function updateVideoCloneRequest(
  requestId: string,
  status: 'pending' | 'processing' | 'completed' | 'rejected',
  resultPrompts: string[] | null,
  adminNotes: string | null,
  adminResponse: string | null
): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase.rpc('admin_update_video_clone_request', {
      p_request_id: requestId,
      p_status: status,
      p_result_prompts: resultPrompts,
      p_admin_notes: adminNotes,
      p_admin_response: adminResponse,
      p_admin_id: user.id
    });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating video clone request:', error);
    return false;
  }
}

/**
 * Get video clone request statistics (admin function)
 */
export async function getVideoCloneStatistics(): Promise<{
  total: number;
  pending: number;
  processing: number;
  completed: number;
  rejected: number;
  totalCreditsUsed: number;
}> {
  try {
    // Single query to get all stats with status and credits
    const { data: allRequests, error } = await supabase
      .from('video_clone_requests')
      .select('status, credits_used');

    if (error) throw error;

    // Calculate stats in memory from single query
    const stats = {
      total: 0,
      pending: 0,
      processing: 0,
      completed: 0,
      rejected: 0,
      totalCreditsUsed: 0
    };

    if (allRequests) {
      for (const req of allRequests) {
        stats.total++;
        stats.totalCreditsUsed += req.credits_used || 0;

        switch (req.status) {
          case 'pending': stats.pending++; break;
          case 'processing': stats.processing++; break;
          case 'completed': stats.completed++; break;
          case 'rejected': stats.rejected++; break;
        }
      }
    }

    return stats;
  } catch (error) {
    console.error('Error getting video clone statistics:', error);
    return {
      total: 0,
      pending: 0,
      processing: 0,
      completed: 0,
      rejected: 0,
      totalCreditsUsed: 0
    };
  }
}

/**
 * Subscribe to video clone request changes for real-time updates
 */
export function subscribeToVideoCloneRequests(
  userId: string,
  callback: (request: VideoCloneRequest) => void
): () => void {
  const channel = supabase
    .channel('video_clone_requests_changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'video_clone_requests',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        if (payload.new) {
          callback(payload.new as VideoCloneRequest);
        }
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
